import { useRef } from "react";
import type { HTSTransformedEvent } from "@b2bportal/core-tracking";
import {
  newTracker,
  trackSelfDescribingEvent,
} from "@snowplow/browser-tracker";
import type { PerkspotSessionInfo } from "@/components/AuthModule/api/auth";
import { useSessionContext } from "@/components/AuthModule/SessionContext";

const VITE_SNOWPLOW_TRACKER_NAME = import.meta.env
  .VITE_SNOWPLOW_TRACKER_NAME as string;
const VITE_SNOWPLOW_COLLECTOR_URL = import.meta.env
  .VITE_SNOWPLOW_COLLECTOR_URL as string;
const VITE_SNOWPLOW_APP_ID = import.meta.env.VITE_SNOWPLOW_APP_ID as string;

const isSnowplowEnabled = (): boolean => {
  return (
    VITE_SNOWPLOW_TRACKER_NAME !== undefined &&
    VITE_SNOWPLOW_COLLECTOR_URL !== undefined &&
    VITE_SNOWPLOW_APP_ID !== undefined
  );
};

const getSessionId = (): string | null => {
  try {
    return sessionStorage.getItem("sessionId");
  } catch {
    return null;
  }
};

const getPsSid = (): string | null => {
  try {
    return sessionStorage.getItem("ps_sid");
  } catch {
    return null;
  }
};

export const initSnowplow = () => {
  if (isSnowplowEnabled()) {
    try {
      // window.snowplow(
      //   "newTracker",
      //   VITE_SNOWPLOW_TRACKER_NAME,
      //   VITE_SNOWPLOW_COLLECTOR_URL,
      //   {
      //     appId: VITE_SNOWPLOW_APP_ID,
      //   }
      // );
      newTracker(VITE_SNOWPLOW_TRACKER_NAME, VITE_SNOWPLOW_COLLECTOR_URL, {
        appId: VITE_SNOWPLOW_APP_ID,
      });
    } catch (error) {
      console.error("Error initializing Snowplow:", error);
    }
  } else {
    console.warn("Snowplow is not properly configured.");
  }
};

const publishSnowplowEvent = async (
  event: HTSTransformedEvent,
  sessionInfo: PerkspotSessionInfo
) => {
  if (isSnowplowEnabled() && sessionInfo?.userDetails) {
    try {
      const psSid = getPsSid();
      trackSelfDescribingEvent({
        event: {
          schema: `iglu:com.perkspot/hopper_travel_event/jsonschema/2-0-2`,
          data: {
            eventType: event.name,
            eventDateTime: new Date().toISOString(),
            externalUserId: sessionInfo.userDetails.tenantUserId,
            communityId: sessionInfo.userDetails.communityId,
            sessionId: getSessionId() ?? "",
            userAgent: navigator.userAgent,
            ...(psSid ? { psSessionId: psSid } : {}),
            ...(event?.properties ?? {}),
          },
        },
      });
    } catch (error) {
      console.error("Error publishing Snowplow event:", error);
    }
  }
};

export const usePublishPerkspotEvent = () => {
  const { sessionInfo } = useSessionContext();
  const sessionInfoRef = useRef(sessionInfo);
  sessionInfoRef.current = sessionInfo;
  return (event: HTSTransformedEvent) => {
    return publishSnowplowEvent(event, sessionInfoRef.current);
  };
};
